import { CredentialType } from "@api/shared-runtime/enums";
import { LICENSE_TYPE_LABELS } from "@api/shared-runtime/credential-vocabularies";
import type { Credential } from "@/features/credentials/types";
import { asDetails } from "@/features/credentials/components/credential-card/credential-details/detail-values";
import { profileText } from "./profile-record-values";
import { getProviderSpecialtyCodes } from "@/features/credentials/lib/provider-specialties";

type ProfileIdentity = {
  firstName: string;
  middleName: string;
  lastName: string;
  suffixName: string;
  professionalType: string | null;
  specialtyCodes: string[];
  specialties: string[];
  npiNumber: string | null;
  address: { city: string | null; state: string | null };
};

export function getProfileIdentity(credentials: Credential[]): ProfileIdentity {
  // Only active holdings belong on this profile. Missing registry placeholders
  // and account fields must not fill gaps in the provider's credential record.
  const heldCredentials = credentials.filter(
    (credential) => credential.present,
  );
  const personalInfo = heldCredentials.find(
    (credential) => credential.credentialType === CredentialType.PersonalInfo,
  );
  const npi = heldCredentials.find(
    (credential) => credential.credentialType === CredentialType.Npi,
  );
  const details = asDetails(personalInfo?.version?.details);
  const name = asDetails(details.name);
  const address = asDetails(details.homeAddress);
  const professional = asDetails(details.professional);
  const providerType =
    profileText(professional.providerType) ??
    profileText(professional.providerRole);
  const professionalType =
    Object.entries(LICENSE_TYPE_LABELS).find(
      ([code]) => code === providerType,
    )?.[1] ?? providerType;
  const specialties = heldCredentials.flatMap((credential) => {
    // An explicit practice selection, including an empty one, takes precedence
    // in the profile header. Certificates remain visible in their own section.
    if (Array.isArray(professional.specialtyCodes)) {
      return [];
    }
    if (credential.credentialType !== CredentialType.BoardCertification) {
      return [];
    }
    const specialty = profileText(
      asDetails(credential.version?.details).specialty,
    );
    return specialty ? [specialty] : [];
  });

  return {
    firstName: profileText(name.first) ?? "",
    middleName: profileText(name.middle) ?? "",
    lastName: profileText(name.last) ?? "",
    suffixName: profileText(name.suffix) ?? "",
    professionalType,
    specialtyCodes: getProviderSpecialtyCodes(credentials),
    specialties: [...new Set(specialties)],
    npiNumber: npi?.version?.number ?? null,
    address: {
      city: profileText(address.city),
      state: profileText(address.state),
    },
  };
}
